/**
 * Doc Processing API client for document management, ingestion, and system health.
 */

import { API_BASE, fetchApi, getAuthHeaders } from './apiClient';
import { useAuthStore } from '../auth/authStore';

// ---- Types ----------------------------------------------------------------

export interface DocumentInfo {
  filename: string;
  gcs_path: string | null;
  category: string;
  file_type: string;
  is_ingested: boolean;
  chunk_count: number;
  linked_modules?: string[];
}

export interface ListDocumentsResponse {
  status: string;
  documents: DocumentInfo[];
  total_storage_files: number;
  total_ingested: number;
  error?: string;
}

export interface UploadResponse {
  status: string;
  gcs_uri?: string;
  blob_path?: string;
  filename?: string;
  category?: string;
  size_bytes?: number;
  error?: string;
}

export interface IngestResponse {
  status: string;
  job_id: string;
  message: string;
}

export interface VerificationResult {
  source_path: string;
  doc_title: string;
  query_used: string;
  found_in_results: boolean;
  rank: number | null;
  total_results: number;
  score: number | null;
  error: string | null;
}

export interface VerificationReport {
  total_documents: number;
  verified_count: number;
  found_count: number;
  not_found_count: number;
  error_count: number;
  pass_rate: number;
  passed: boolean;
  duration_ms: number;
  sampled?: boolean;
  results: VerificationResult[];
}

// ── Ingestion Health (extended) ──────────────────────────────

export type HealthStatus = 'healthy' | 'warning' | 'error' | 'info';

export interface ComponentHealth {
  status: HealthStatus;
  [key: string]: unknown;
}

export interface EmbeddingHealth extends ComponentHealth {
  total_chunks: number;
  embedded_chunks: number;
  null_embedding_chunks: number;
  coverage_pct: number;
  unique_documents: number;
  documents_with_embeddings: number;
  zero_vectors_detected: number;
  configured_dimension: number;
}

export interface BM25Health extends ComponentHealth {
  ready: boolean;
  indexed_chunks: number;
  indexed_documents: number;
  coverage_pct: number;
}

export interface FrontmatterHealth extends ComponentHealth {
  total_entries: number;
  documents_missing_frontmatter: number;
  incomplete_entries: number;
  coverage_pct: number;
}

export interface DomainTermsHealth extends ComponentHealth {
  total_terms: number;
  total_synonyms: number;
  cache_terms: number;
}

export interface ChunkExpansionHealth extends ComponentHealth {
  expanded_chunks: number;
  chunks_without_expansion: number;
  effective_expansions: number;
  empty_expansions: number;
  coverage_pct: number;
  effectiveness_pct: number;
}

export interface FlowDiagramHealth extends ComponentHealth {
  total_diagrams: number;
  chunks_with_flow_summary: number;
  flow_chunks_missing_embedding: number;
}

export interface KnowledgeGraphHealth extends ComponentHealth {
  loaded: boolean;
  node_count: number;
  edge_count: number;
  alias_count: number;
}

export interface RaptorHealth extends ComponentHealth {
  llm_configured: boolean;
  model: string;
}

export interface TrainedQaHealth extends ComponentHealth {
  total_answers: number;
  active_answers: number;
  total_variants: number;
  variants_with_embeddings: number;
  embedding_coverage_pct: number;
}

export interface SchemaGraphHealth extends ComponentHealth {
  neo4j_connected: boolean;
  schema_graph_loaded: boolean;
  docs_linked: number;
  total_links: number;
}

export interface DocumentSourcesHealth extends ComponentHealth {
  total_chunks: number;
  json_loaded_chunks: number;
  doc_converted_chunks: number;
  unique_documents: number;
}

export interface IngestionHealthReport {
  embeddings: EmbeddingHealth;
  bm25_index: BM25Health;
  frontmatter: FrontmatterHealth;
  domain_terms: DomainTermsHealth;
  chunk_expansions: ChunkExpansionHealth;
  flow_diagrams: FlowDiagramHealth;
  knowledge_graph: KnowledgeGraphHealth;
  raptor_summaries: RaptorHealth;
  trained_qa?: TrainedQaHealth;
  schema_graph?: SchemaGraphHealth;
  document_sources?: DocumentSourcesHealth;
}

export interface ReindexSummary {
  documents_processed: number;
  pass_rate: number;
}

export interface FrontmatterEntry {
  id: number;
  gcs_path: string;
  filename: string;
  category: string | null;
  summary: string;
  module: string | null;
  feature: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface FrontmatterListResponse {
  entries: FrontmatterEntry[];
  total: number;
}

export interface FrontmatterUpsertRequest {
  gcs_path: string;
  filename: string;
  summary: string;
  category?: string | null;
  module?: string | null;
  feature?: string | null;
}

export interface BulkGenerateDocItem {
  gcs_path: string;
  filename: string;
  category?: string | null;
}

export interface BulkGenerateEvent {
  index?: number;
  filename?: string;
  status: 'started' | 'in_progress' | 'completed' | 'error' | 'complete';
  total: number;
  result?: FrontmatterEntry;
  /** Snapshot of the existing row before generation — present on completed
   *  events (both real runs and dry-runs) so the UI can render an old-vs-new
   *  diff. `null` when the document has no existing frontmatter row. */
  previous?: {
    summary: string;
    module: string | null;
    feature: string | null;
  } | null;
  /** Echoed from the request on the `started` event and from each completed
   *  event when the run did not persist. */
  dry_run?: boolean;
  /** Count of modules pulled from the Neo4j catalogue, emitted on `started`. */
  known_modules_count?: number;
  error?: string;
  succeeded?: number;
  failed?: number;
}

// ── Domain Terms ──────────────────────────────────────────────

export interface DomainTerm {
  id: number;
  term: string;
  canonical_form: string;
  category: string | null;
  is_abbreviation: boolean;
  full_form: string | null;
  source: string | null;
  confidence: number | null;
  verified: boolean | null;
  usage_count: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DomainTermListResponse {
  entries: DomainTerm[];
  total: number;
}

export interface DomainTermUpsertRequest {
  id?: number;
  term: string;
  canonical_form: string;
  category?: string | null;
  is_abbreviation?: boolean;
  full_form?: string | null;
  source?: string | null;
  confidence?: number | null;
  verified?: boolean;
}

// ── Synonym Mappings ──────────────────────────────────────────

export interface SynonymMapping {
  id: number;
  term_id: number;
  synonym: string;
  relationship_type: string | null;
  confidence: number | null;
  source: string | null;
  usage_count: number | null;
  success_rate: number | null;
  verified: boolean | null;
  created_at: string | null;
  term: string | null; // joined from domain_terms
}

export interface SynonymMappingListResponse {
  entries: SynonymMapping[];
  total: number;
}

export interface SynonymMappingUpsertRequest {
  id?: number;
  term_id: number;
  synonym: string;
  relationship_type?: string | null;
  confidence?: number | null;
  source?: string | null;
  verified?: boolean;
}

// ── Image to Flow (Mermaid diagrams) ────────────────────────

export interface FlowDiagram {
  id: number;
  title: string;
  mermaid_code: string;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface FlowDiagramListResponse {
  entries: FlowDiagram[];
  total: number;
}

export interface FlowDiagramSaveRequest {
  title: string;
  mermaid_code: string;
  description?: string | null;
  /** When provided, appends the flow diagram as an indexed chunk to this document. */
  source_path?: string | null;
  /** When provided, appends the flow diagram to each of these documents (multi-select). */
  source_paths?: string[];
}

export interface FlowDiagramUpdateRequest {
  title?: string;
  mermaid_code?: string;
  description?: string | null;
}

// ---- Helpers --------------------------------------------------------------

async function getAuthHeadersForUpload(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  try {
    const token = await useAuthStore.getState().refreshToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (err) {
    console.warn('[DocProcessingAPI] Failed to get auth token:', err);
  }
  return headers;
}

// ---- API ------------------------------------------------------------------

export const docProcessingApi = {
  /** Upload a document to Hetzner object storage. */
  async uploadDocument(file: File, category: string): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);

    const headers = await getAuthHeadersForUpload();
    // Note: do NOT set Content-Type — browser sets it with boundary for multipart
    const response = await fetchApi(`${API_BASE}/doc-processing/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Upload failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  /** List all documents with their ingestion status. */
  async listDocuments(category?: string): Promise<ListDocumentsResponse> {
    const headers = await getAuthHeaders();
    const params = category ? `?category=${encodeURIComponent(category)}` : '';
    const response = await fetchApi(`${API_BASE}/doc-processing/documents${params}`, {
      headers,
    });
    if (!response.ok) throw new Error(`List documents failed: ${response.status}`);
    return response.json();
  },

  /** Trigger ingestion from a path (server-side). */
  async ingestDocuments(
    sourcePath: string,
    docType: string,
    category: string,
    forceReindex: boolean,
  ): Promise<IngestResponse> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/ingest`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        source_path: sourcePath,
        doc_type: docType,
        category,
        force_reindex: forceReindex,
      }),
    });
    if (!response.ok) throw new Error(`Ingest failed: ${response.status}`);
    return response.json();
  },

  /** Full re-index: download all from object storage and re-ingest. */
  async fullReindex(): Promise<{
    status: string;
    job_id: string;
    total: number;
    message: string;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(
      `${API_BASE}/doc-processing/reindex`,
      { method: 'POST', headers },
    );
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Reindex failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  /** Rebuild BM25 search index. */
  async rebuildBm25(): Promise<{ status: string; message: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/rebuild-bm25`, {
      method: 'POST',
      headers,
    });
    if (!response.ok) throw new Error(`BM25 rebuild failed: ${response.status}`);
    return response.json();
  },

  /** Run post-ingestion verification. */
  async verifyIngestion(): Promise<VerificationReport> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/verify-ingestion`, {
      method: 'POST',
      headers,
    });
    if (!response.ok) throw new Error(`Verification failed: ${response.status}`);
    return response.json();
  },

  /** Get extended ingestion health report across all pipeline components. */
  async getIngestionHealth(): Promise<IngestionHealthReport> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/ingestion-health`, {
      headers,
    });
    if (!response.ok) throw new Error(`Ingestion health check failed: ${response.status}`);
    return response.json();
  },

  /** Poll a background job until it completes or fails. */
  async pollJobStatus(
    jobId: string,
    intervalMs = 2000,
    maxAttempts = 120,
    onProgress?: (job: { status: string; total: number; completed: number; failed: number }) => void,
  ): Promise<{
    status: string;
    total: number;
    completed: number;
    failed: number;
    errors: string[];
  }> {
    for (let i = 0; i < maxAttempts; i++) {
      // Refresh auth headers each iteration to avoid stale tokens during long polls.
      const headers = await getAuthHeaders();
      const response = await fetchApi(`${API_BASE}/doc-processing/jobs/${encodeURIComponent(jobId)}`, {
        headers,
      });
      if (!response.ok) throw new Error(`Job status check failed: ${response.status}`);
      const job = await response.json();
      if (onProgress) onProgress(job);
      if (job.status === 'completed' || job.status === 'failed') {
        return job;
      }
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    throw new Error('Job polling timed out');
  },

  /** Re-index specific documents by their storage paths. */
  async reindexSelected(gcsPaths: string[]): Promise<{
    status: string;
    job_id: string;
    total: number;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/reindex-selected`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ gcs_paths: gcsPaths }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Selective reindex failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  /** Trigger document-to-schema module linking via Neo4j (background job).
   *  mode: "keyword" (fast) or "llm" (accurate, uses LLM).
   *  Returns immediately — poll getLinkStatus() for progress. */
  async relinkModules(mode: 'keyword' | 'llm' = 'keyword'): Promise<{
    status: string;
    message?: string;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/reasoning/link-docs-to-schema`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ mode }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Module linking failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  /** Poll the status of the document-to-schema linking job. */
  async getLinkStatus(): Promise<{
    status: 'running' | 'completed' | 'not_run';
    chunks_processed?: number;
    total_chunks?: number;
    documents_created?: number;
    links_created?: number;
    skipped?: number;
    extraction_mode?: string;
    errors?: string[];
    error_count?: number;
    message?: string;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/reasoning/link-status`, { headers });
    if (!response.ok) throw new Error(`Link status failed: ${response.status}`);
    return response.json();
  },

  /** Get system stats. */
  async getStats(): Promise<Record<string, unknown>> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/stats`, { headers });
    if (!response.ok) throw new Error(`Stats failed: ${response.status}`);
    return response.json();
  },

  // ---- Frontmatter Management ----

  /** List all frontmatter entries. Fetches all pages to ensure complete merge. */
  async listFrontmatter(category?: string): Promise<FrontmatterListResponse> {
    const headers = await getAuthHeaders();
    const baseParams = category ? `category=${encodeURIComponent(category)}&` : '';

    // Fetch first page with a large limit
    const pageSize = 500;
    let offset = 0;
    let allEntries: FrontmatterEntry[] = [];

    while (true) {
      const url = `${API_BASE}/doc-processing/frontmatter?${baseParams}limit=${pageSize}&offset=${offset}`;
      const response = await fetchApi(url, { headers });
      if (!response.ok) throw new Error(`List frontmatter failed: ${response.status}`);
      const page: FrontmatterListResponse = await response.json();
      allEntries = allEntries.concat(page.entries);

      // If we got fewer than pageSize entries, we've fetched everything
      if (page.entries.length < pageSize || allEntries.length >= page.total) {
        return { entries: allEntries, total: page.total };
      }
      offset += pageSize;
    }
  },

  /** Get frontmatter for a specific document. */
  async getFrontmatter(gcsPath: string): Promise<FrontmatterEntry> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(
      `${API_BASE}/doc-processing/frontmatter/lookup?gcs_path=${encodeURIComponent(gcsPath)}`,
      { headers },
    );
    if (!response.ok) {
      if (response.status === 404) throw new Error('not_found');
      throw new Error(`Get frontmatter failed: ${response.status}`);
    }
    return response.json();
  },

  /** Create or update frontmatter for a document. */
  async upsertFrontmatter(data: FrontmatterUpsertRequest): Promise<{ status: string; entry: FrontmatterEntry }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/frontmatter`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Save frontmatter failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  /** Generate frontmatter for multiple documents using Claude (SSE streaming).
   *  Set `dryRun` to preview proposed values without writing — the SSE events
   *  include a `previous` snapshot so the caller can show old-vs-new diffs. */
  async generateFrontmatterBulk(
    documents: BulkGenerateDocItem[],
    onEvent: (event: BulkGenerateEvent) => void,
    dryRun: boolean = false,
  ): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/frontmatter/generate-bulk`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ documents, dry_run: dryRun }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Bulk generate failed: ${response.status} — ${text}`);
    }

    if (!response.body) {
      throw new Error('Bulk generate failed: no response body for streaming');
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop()!;

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event: BulkGenerateEvent = JSON.parse(line.slice(6));
            onEvent(event);
          } catch {
            // skip malformed events
          }
        }
      }
    }
  },

  /** Delete frontmatter for a document. */
  async deleteFrontmatter(gcsPath: string): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(
      `${API_BASE}/doc-processing/frontmatter?gcs_path=${encodeURIComponent(gcsPath)}`,
      { method: 'DELETE', headers },
    );
    if (!response.ok) throw new Error(`Delete frontmatter failed: ${response.status}`);
    return response.json();
  },

  // ---- Domain Terms Management ----

  async listDomainTerms(): Promise<DomainTermListResponse> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/domain-terms`, { headers });
    if (!response.ok) throw new Error(`List domain terms failed: ${response.status}`);
    return response.json();
  },

  async upsertDomainTerm(data: DomainTermUpsertRequest): Promise<{ status: string; entry: DomainTerm }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/domain-terms`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Save domain term failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  async deleteDomainTerm(id: number): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(
      `${API_BASE}/doc-processing/domain-terms?id=${id}`,
      { method: 'DELETE', headers },
    );
    if (!response.ok) throw new Error(`Delete domain term failed: ${response.status}`);
    return response.json();
  },

  // ---- Synonym Mappings Management ----

  async listSynonymMappings(): Promise<SynonymMappingListResponse> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/synonym-mappings`, { headers });
    if (!response.ok) throw new Error(`List synonym mappings failed: ${response.status}`);
    return response.json();
  },

  async upsertSynonymMapping(data: SynonymMappingUpsertRequest): Promise<{ status: string; entry: SynonymMapping }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/synonym-mappings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Save synonym mapping failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  async deleteSynonymMapping(id: number): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(
      `${API_BASE}/doc-processing/synonym-mappings?id=${id}`,
      { method: 'DELETE', headers },
    );
    if (!response.ok) throw new Error(`Delete synonym mapping failed: ${response.status}`);
    return response.json();
  },

  // ---- Bulk Export / Import / Wipe ----

  async exportDomainTerms(): Promise<{ entries: any[]; total_terms: number; total_synonyms: number }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/domain-terms/export`, { headers });
    if (!response.ok) throw new Error(`Export failed: ${response.status}`);
    return response.json();
  },

  async wipeDomainTerms(): Promise<{ status: string; terms_deleted: number; chunk_expansions_deleted: number }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/domain-terms/all`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) throw new Error(`Wipe failed: ${response.status}`);
    return response.json();
  },

  async importDomainTerms(entries: any[]): Promise<{ status: string; terms_created: number; synonyms_created: number; errors: string[] }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/domain-terms/import`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ entries }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Import failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  /** Bulk delete domain terms by source and/or category filter. */
  async bulkDeleteDomainTerms(source?: string, category?: string): Promise<{ status: string; terms_deleted: number; filter: { source: string | null; category: string | null } }> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (source) params.set('source', source);
    if (category) params.set('category', category);
    const response = await fetchApi(`${API_BASE}/doc-processing/domain-terms/bulk?${params.toString()}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Bulk delete failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  // ---- Image to Flow (Mermaid Diagrams) ----

  /** Convert a pasted image to Mermaid code via Claude vision. */
  async convertImageToFlow(imageBase64: string, mimeType: string): Promise<{ mermaid_code: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/image-to-flow/convert`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ image: imageBase64, mime_type: mimeType }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Image to flow conversion failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  /** Fix broken Mermaid code via Claude. */
  async fixMermaidCode(mermaidCode: string, errorMessage?: string): Promise<{ mermaid_code: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/image-to-flow/fix`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ mermaid_code: mermaidCode, error_message: errorMessage || null }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Fix mermaid code failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  /** Save a flow diagram. */
  async saveFlowDiagram(data: FlowDiagramSaveRequest): Promise<{ status: string; entry: FlowDiagram }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/image-to-flow`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Save flow diagram failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  /** List all saved flow diagrams. */
  async listFlowDiagrams(): Promise<FlowDiagramListResponse> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/image-to-flow`, { headers });
    if (!response.ok) throw new Error(`List flow diagrams failed: ${response.status}`);
    return response.json();
  },

  /** Update a flow diagram. */
  async updateFlowDiagram(id: number, data: FlowDiagramUpdateRequest): Promise<{ status: string; entry: FlowDiagram }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/doc-processing/image-to-flow/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Update flow diagram failed: ${response.status} — ${text}`);
    }
    return response.json();
  },

  /** Delete a flow diagram. */
  async deleteFlowDiagram(id: number): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(
      `${API_BASE}/doc-processing/image-to-flow?id=${id}`,
      { method: 'DELETE', headers },
    );
    if (!response.ok) throw new Error(`Delete flow diagram failed: ${response.status}`);
    return response.json();
  },

  // ── Concept Graph ────────────────────────────────────────────────

  async listConceptNodes(): Promise<{ nodes: ConceptNode[]; total: number }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/nodes`, { headers });
    if (!r.ok) throw new Error(`List concept nodes failed: ${r.status}`);
    return r.json();
  },

  async createConceptNode(node: ConceptNode): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/nodes`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(node),
    });
    if (!r.ok) throw new Error(`Create concept node failed: ${r.status}`);
    return r.json();
  },

  async updateConceptNode(nodeId: string, update: Partial<ConceptNode>): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/nodes/${nodeId}`, {
      method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(update),
    });
    if (!r.ok) throw new Error(`Update concept node failed: ${r.status}`);
    return r.json();
  },

  async deleteConceptNode(nodeId: string): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/nodes/${nodeId}`, {
      method: 'DELETE', headers,
    });
    if (!r.ok) throw new Error(`Delete concept node failed: ${r.status}`);
    return r.json();
  },

  async listConceptEdges(): Promise<{ edges: ConceptEdge[]; total: number }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/edges`, { headers });
    if (!r.ok) throw new Error(`List concept edges failed: ${r.status}`);
    return r.json();
  },

  async createConceptEdge(edge: { from: string; to: string; relation: string }): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/edges`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(edge),
    });
    if (!r.ok) throw new Error(`Create concept edge failed: ${r.status}`);
    return r.json();
  },

  async deleteConceptEdge(from: string, to: string, relation: string): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(
      `${API_BASE}/doc-processing/concept-graph/edges?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&relation=${encodeURIComponent(relation)}`,
      { method: 'DELETE', headers },
    );
    if (!r.ok) throw new Error(`Delete concept edge failed: ${r.status}`);
    return r.json();
  },

  async getConceptGraphStats(): Promise<ConceptGraphStats> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/stats`, { headers });
    if (!r.ok) throw new Error(`Get concept graph stats failed: ${r.status}`);
    return r.json();
  },

  async reloadConceptGraph(): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/reload`, { method: 'POST', headers });
    if (!r.ok) throw new Error(`Reload concept graph failed: ${r.status}`);
    return r.json();
  },

  async saveConceptGraph(): Promise<{ status: string }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/save`, { method: 'POST', headers });
    if (!r.ok) throw new Error(`Save concept graph failed: ${r.status}`);
    return r.json();
  },

  async generateConceptGraphBatch(autoAccept: boolean = true, chunksPerDoc: number = 3): Promise<{ status: string; job_id: string; message: string }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/generate-batch`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ auto_accept: autoAccept, chunks_per_doc: chunksPerDoc }),
    });
    if (!r.ok) throw new Error(`Batch generate failed: ${r.status}`);
    return r.json();
  },

  async getJobStatus(jobId: string): Promise<{ job_id: string; status: string; total: number; completed: number; failed: number; errors: string[] }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/jobs/${jobId}`, { headers });
    if (!r.ok) throw new Error(`Get job status failed: ${r.status}`);
    return r.json();
  },

  async generateConceptGraph(sampleSize: number = 50): Promise<{ status: string; job_id: string; message: string }> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/generate`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ sample_size: sampleSize }),
    });
    if (!r.ok) throw new Error(`Generate concept graph failed: ${r.status}`);
    return r.json();
  },

  async exportConceptGraph(): Promise<any> {
    const headers = await getAuthHeaders();
    const r = await fetchApi(`${API_BASE}/doc-processing/concept-graph/export`, { headers });
    if (!r.ok) throw new Error(`Export concept graph failed: ${r.status}`);
    return r.json();
  },
};

// ── Concept Graph types ──────────────────────────────────────────

export interface ConceptNode {
  id: string;
  name: string;
  node_type: string;
  description: string;
  aliases: string[];
  module: string;
}

export interface ConceptEdge {
  from: string;
  from_name: string;
  to: string;
  to_name: string;
  relation: string;
}

export interface ConceptGraphStats {
  node_count: number;
  edge_count: number;
  alias_count: number;
}

export interface CandidateNode {
  id: string;
  name: string;
  node_type: string;
  description: string;
  aliases: string[];
  module: string;
  is_new: boolean;
}

export interface CandidateEdge {
  from: string;
  to: string;
  relation: string;
  is_new: boolean;
}

export interface GenerateConceptGraphResponse {
  status: string;
  candidates: {
    nodes: CandidateNode[];
    edges: CandidateEdge[];
  };
  summary: {
    chunks_sampled: number;
    total_nodes_found: number;
    new_nodes: number;
    total_edges_found: number;
    new_edges: number;
  };
}
